// Human formatting for the admin: money, numbers, dates (store timezone), relative times.
// The StoreShell calls setFormatDefaults() with the current store's currency/locale/timezone, so
// pages can call `money(order.total)` or `date(order.createdAt)` without threading the store
// through every component. Pass explicit arguments when formatting another store's data
// (platform pages) or a foreign currency.
//
// Rules: money is ALWAYS integer minor units in and out (shared/money.ts); a store "day" is
// computed in the store timezone (dayKey), never with toISOString().
import { formatMoney, currencyDecimals, toMajor } from '../../../shared/money'

export interface FormatDefaults { currency: string; locale: string; timeZone: string }

const browserTz = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC' } catch { return 'UTC' }
})()

const defaults: FormatDefaults = { currency: 'USD', locale: 'en', timeZone: browserTz }

/** Called by the store shell when the current store loads/changes. */
export function setFormatDefaults(next: Partial<FormatDefaults>) {
  if (next.currency) defaults.currency = next.currency.toUpperCase()
  if (next.locale) defaults.locale = safeLocale(next.locale)
  if (next.timeZone) defaults.timeZone = safeTimeZone(next.timeZone)
}
export function getFormatDefaults(): Readonly<FormatDefaults> { return defaults }

function safeLocale(l: string): string {
  try { return Intl.NumberFormat.supportedLocalesOf([l]).length ? l : 'en' } catch { return 'en' }
}
function safeTimeZone(tz: string): string {
  try { new Intl.DateTimeFormat('en', { timeZone: tz }); return tz } catch { return browserTz }
}

// Intl constructors are slow; cache them.
const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat | Intl.PluralRules>()
function cached<T>(key: string, make: () => T): T {
  let v = cache.get(key) as T | undefined
  if (!v) { v = make(); cache.set(key, v as any) }
  return v
}

// ─── Money ──────────────────────────────────────────────────────────────────────────────

/** `money(199900, 'INR')` → "₹1,999.00". Minor units in; currency defaults to the store's. */
export function money(minor: number | null | undefined, currency = defaults.currency, locale = defaults.locale): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '—'
  return formatMoney(minor, currency, locale)
}

/** Compact money for KPI tiles and chart axes: "₹1.2L" / "$12.4K". */
export function moneyCompact(minor: number | null | undefined, currency = defaults.currency, locale = defaults.locale): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return '—'
  const major = toMajor(minor, currency)
  if (Math.abs(major) < 10_000) return formatMoney(minor, currency, locale)
  try {
    return cached(`mc|${currency}|${locale}`, () => new Intl.NumberFormat(locale, {
      style: 'currency', currency, notation: 'compact', maximumFractionDigits: 1,
    })).format(major)
  } catch { return formatMoney(minor, currency, locale) }
}

/** Currency symbol for input prefixes ("₹", "$", "CHF"). */
export function currencySymbol(currency = defaults.currency, locale = defaults.locale): string {
  try {
    const parts = cached(`cs|${currency}|${locale}`, () => new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: 'narrowSymbol' })).formatToParts(0)
    return parts.find(p => p.type === 'currency')?.value ?? currency
  } catch { return currency }
}

/** Minor units → the plain editable string for an input ("1999.00" → shown as "1,999.00"). */
export function minorToInput(minor: number | null | undefined, currency = defaults.currency, locale = defaults.locale, grouping = true): string {
  if (minor === null || minor === undefined || !Number.isFinite(minor)) return ''
  const d = currencyDecimals(currency)
  return cached(`mi|${d}|${locale}|${grouping}`, () => new Intl.NumberFormat(locale, {
    minimumFractionDigits: d, maximumFractionDigits: d, useGrouping: grouping,
  })).format(toMajor(minor, currency))
}

/**
 * Parse what a merchant typed into minor units, tolerant of the locale's separators:
 * "1,999.50" (en), "1.999,50" (de), "1 999,50" (fr), "12,34,567" (en-IN lakh grouping).
 * A currency symbol/code may lead or trail ("₹ 1,999", "Rs. 1,999", "1999 INR", "US$5", "-$5");
 * a minus is accepted only in front. Anything else — letters or signs inside the number ("5-3",
 * "12abc34", "1e3"), broken grouping ("12,3,456"), two decimal marks — returns NaN; empty returns
 * null. Exact decimal arithmetic (no float drift); rounds half away from zero to the minor unit.
 */
export function parseMoneyInput(text: string, currency = defaults.currency, locale = defaults.locale): number | null {
  let s = text.normalize('NFKC').trim()
  if (!s) return null
  let neg = false
  const takeMinus = () => { if (/^[-\u2212\u2012\u2013]/.test(s)) { neg = true; s = s.slice(1).trim() } }
  takeMinus()
  s = stripCurrencyAffix(s, currency, locale, 'start')
  if (!neg) takeMinus()
  s = stripCurrencyAffix(s, currency, locale, 'end')
  // Only digits, separators and grouping spaces/apostrophes remain in a valid amount.
  if (!/\d/.test(s) || !/^[\d.,\s'\u00a0\u202f\u2019]+$/.test(s)) return NaN
  const { group, decimal } = separators(locale)
  // Spaces, NBSP, narrow NBSP and apostrophes are grouping marks; normalise them to one char.
  s = s.replace(/[\s\u00a0\u202f'\u2019]/g, ' ')
  if (/ {2}/.test(s)) return NaN
  const lastDot = s.lastIndexOf('.'), lastComma = s.lastIndexOf(',')
  let decSep: string | null = null
  if (lastDot >= 0 && lastComma >= 0) decSep = lastDot > lastComma ? '.' : ','
  else if (lastDot >= 0 || lastComma >= 0) {
    const sep = lastDot >= 0 ? '.' : ','
    const count = s.split(sep).length - 1
    const after = s.length - s.lastIndexOf(sep) - 1
    // A single separator followed by exactly 3 digits is ambiguous ("1,000"): trust the locale.
    if (count > 1) decSep = null
    else if (after === 3 && (sep === group || (group !== '.' && group !== ',' && sep !== decimal))) decSep = null
    else if (sep === decimal || after !== 3) decSep = sep
    else decSep = null
  }
  let intPart = s, frac = ''
  if (decSep) { const i = s.lastIndexOf(decSep); intPart = s.slice(0, i); frac = s.slice(i + 1) }
  if (/[^\d]/.test(frac)) return NaN
  // Grouping must be real grouping: one separator char, not the decimal one, groups of 3
  // (or Indian 2-digit groups like 12,34,567) — "1.2.3.4,5,6" is a typo, not 12345.6.
  const groupChars = intPart.replace(/\d/g, '')
  if (groupChars) {
    const g = groupChars[0]
    if ((decSep && groupChars.includes(decSep)) || [...groupChars].some(c => c !== g)) return NaN
    const esc = g === '.' ? '\\.' : g
    if (!new RegExp(`^\\d{1,3}(?:${esc}\\d{2,3})*${esc}\\d{3}$`).test(intPart)) return NaN
    intPart = intPart.split(g).join('')
  }
  if (!/^\d*$/.test(intPart) || (!intPart && !frac)) return NaN
  // Exact: integer digits * 10^d + the first d fraction digits, rounding on the next digit.
  const d = currencyDecimals(currency)
  const digits = (intPart.replace(/^0+(?=\d)/, '') || '0') + (frac + '0'.repeat(d)).slice(0, d)
  if (digits.length > 16) return NaN
  let minor = Number(digits)
  if (frac.length > d && Number(frac[d]) >= 5) minor += 1
  if (!Number.isSafeInteger(minor)) return NaN
  return neg && minor !== 0 ? -minor : minor
}

/** Remove one currency symbol / code at the start or end ("₹", "$", "US$", "Rs.", "INR", "CHF"). */
function stripCurrencyAffix(s: string, currency: string, locale: string, side: 'start' | 'end'): string {
  const t = s
  for (const tok of currencyTokens(currency, locale)) {
    if (side === 'start' && t.toLowerCase().startsWith(tok.toLowerCase())) return t.slice(tok.length).trim()
    if (side === 'end' && t.toLowerCase().endsWith(tok.toLowerCase())) return t.slice(0, t.length - tok.length).trim()
  }
  // Any currency sign (\p{Sc}: $ € £ ¥ ₹ ₦ …), optionally country-prefixed ("US$", "A$", "R$").
  if (side === 'start' && /^[A-Za-z]{0,3}\p{Sc}/u.test(t)) return t.replace(/^[A-Za-z]{0,3}\p{Sc}/u, '').trim()
  if (side === 'end' && /[A-Za-z]{0,3}\p{Sc}$/u.test(t)) return t.replace(/[A-Za-z]{0,3}\p{Sc}$/u, '').trim()
  return t
}

const RUPEE_ALIASES = ['Rs.', 'Rs', '₨', 'रु.', 'रु']
function currencyTokens(currency: string, locale: string): string[] {
  const key = `ct|${currency}|${locale}`
  let v = tokenCache.get(key)
  if (!v) {
    const set = new Set<string>([currency.toUpperCase()])
    for (const display of ['narrowSymbol', 'symbol'] as const) {
      try {
        const sym = new Intl.NumberFormat(locale, { style: 'currency', currency, currencyDisplay: display }).formatToParts(0).find(p => p.type === 'currency')?.value
        if (sym) set.add(sym.normalize('NFKC'))
      } catch { /* unknown currency */ }
    }
    if (['INR', 'PKR', 'LKR', 'NPR', 'MUR', 'SCR'].includes(currency.toUpperCase())) RUPEE_ALIASES.forEach(a => set.add(a))
    // Longest first so "US$" wins over "$" and "Rs." over "Rs".
    v = [...set].filter(Boolean).sort((a, b) => b.length - a.length)
    tokenCache.set(key, v)
  }
  return v
}
const tokenCache = new Map<string, string[]>()

function separators(locale: string): { group: string; decimal: string } {
  return cached(`sep|${locale}`, () => {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6)
    return {
      group: parts.find(p => p.type === 'group')?.value ?? ',',
      decimal: parts.find(p => p.type === 'decimal')?.value ?? '.',
    }
  }) as unknown as { group: string; decimal: string }
}

// ─── Numbers ────────────────────────────────────────────────────────────────────────────

/** `number(12345.6)` → "12,345.6". */
export function number(n: number | null | undefined, opts: Intl.NumberFormatOptions = {}, locale = defaults.locale): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  return cached(`n|${locale}|${JSON.stringify(opts)}`, () => new Intl.NumberFormat(locale, { maximumFractionDigits: 2, ...opts })).format(n)
}

/** `compactNumber(12400)` → "12.4K". */
export function compactNumber(n: number | null | undefined, locale = defaults.locale): string {
  return number(n, { notation: 'compact', maximumFractionDigits: 1 }, locale)
}

/**
 * `percent(0.1234)` → "12.3%". Pass a ratio (0–1). For basis points use `percent(bp / 10000)`.
 * `signed: true` prefixes "+" for positive deltas.
 */
export function percent(ratio: number | null | undefined, opts: { digits?: number; signed?: boolean } = {}, locale = defaults.locale): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—'
  const digits = opts.digits ?? (Math.abs(ratio) < 0.1 && ratio !== 0 ? 1 : Math.abs(ratio) >= 1 ? 0 : 1)
  return cached(`p|${locale}|${digits}|${!!opts.signed}`, () => new Intl.NumberFormat(locale, {
    style: 'percent', minimumFractionDigits: 0, maximumFractionDigits: digits, signDisplay: opts.signed ? 'exceptZero' : 'auto',
  })).format(ratio)
}

/** `bytes(1536)` → "1.5 KB". */
export function bytes(n: number | null | undefined, locale = defaults.locale): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let i = 0, v = Math.abs(n)
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++ }
  return `${number(Math.sign(n) * v, { maximumFractionDigits: i === 0 ? 0 : 1 }, locale)} ${units[i]}`
}

/**
 * `pluralize(3, 'order')` → "3 orders"; `pluralize(1, 'person', 'people')` → "1 person".
 * `pluralize(2, 'item', undefined, false)` → "items" (word only).
 */
export function pluralize(count: number, singular: string, plural?: string, withCount = true, locale = defaults.locale): string {
  const rule = cached(`pr|${locale}`, () => new Intl.PluralRules(locale)) as Intl.PluralRules
  const word = rule.select(count) === 'one' ? singular : (plural ?? defaultPlural(singular))
  return withCount ? `${number(count, {}, locale)} ${word}` : word
}
function defaultPlural(w: string): string {
  if (/[^aeiou]y$/i.test(w)) return w.slice(0, -1) + 'ies'
  if (/(s|x|z|ch|sh)$/i.test(w)) return w + 'es'
  return w + 's'
}

// ─── Dates ──────────────────────────────────────────────────────────────────────────────

export type DateInput = string | number | Date | null | undefined

export function toDate(d: DateInput): Date | null {
  if (d === null || d === undefined || d === '') return null
  const v = d instanceof Date ? d : new Date(d)
  return Number.isNaN(v.getTime()) ? null : v
}

type DateStyle = 'short' | 'medium' | 'long' | 'datetime' | 'time' | 'full'
const DATE_OPTS: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  short: { day: 'numeric', month: 'short' },                                    // 26 Sep
  medium: { day: 'numeric', month: 'short', year: 'numeric' },                  // 26 Sep 2026
  long: { day: 'numeric', month: 'long', year: 'numeric' },                     // 26 September 2026
  datetime: { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' },
  time: { hour: 'numeric', minute: '2-digit' },
  full: { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' },
}

/**
 * `date(order.createdAt)` → "26 Sep 2026" in the STORE timezone. `style`: short | medium (default) |
 * long | datetime | time | full. Short omits the year only when it is the current year.
 */
export function date(d: DateInput, tz = defaults.timeZone, style: DateStyle = 'medium', locale = defaults.locale): string {
  const v = toDate(d)
  if (!v) return '—'
  let opts = DATE_OPTS[style]
  if (style === 'short' && dayKey(v, tz).slice(0, 4) !== dayKey(new Date(), tz).slice(0, 4)) opts = DATE_OPTS.medium
  return cached(`d|${locale}|${tz}|${style}|${opts === DATE_OPTS.medium}`, () => new Intl.DateTimeFormat(locale, { ...opts, timeZone: tz })).format(v)
}

/** `dateTime(x)` → "26 Sep 2026, 3:04 pm" in the store timezone. */
export function dateTime(d: DateInput, tz = defaults.timeZone, locale = defaults.locale): string {
  return date(d, tz, 'datetime', locale)
}

/** The calendar day of an instant in a timezone, as "YYYY-MM-DD". Use this — never toISOString() — to bucket by store day. */
export function dayKey(d: DateInput, tz = defaults.timeZone): string {
  const v = toDate(d)
  if (!v) return ''
  const parts = (cached(`dk|${tz}`, () => new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' })) as Intl.DateTimeFormat).formatToParts(v)
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '00'
  return `${get('year')}-${get('month')}-${get('day')}`
}

/** Today's "YYYY-MM-DD" in the store timezone. */
export function today(tz = defaults.timeZone): string { return dayKey(new Date(), tz) }

/** Add days to a "YYYY-MM-DD" key (calendar arithmetic, no timezone drift). */
export function addDays(key: string, days: number): string {
  const [y, m, d] = key.split('-').map(Number)
  const t = new Date(Date.UTC(y, m - 1, d + days))
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`
}

/** Format a "YYYY-MM-DD" day key for humans without shifting it through a timezone. */
export function dayLabel(key: string, style: 'short' | 'medium' | 'long' = 'medium', locale = defaults.locale): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key)
  if (!m) return '—'
  const v = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], 12))
  return cached(`dl|${locale}|${style}`, () => new Intl.DateTimeFormat(locale, { ...DATE_OPTS[style], timeZone: 'UTC' })).format(v)
}

const REL_UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ['year', 365 * 24 * 3600], ['month', 30 * 24 * 3600], ['week', 7 * 24 * 3600],
  ['day', 24 * 3600], ['hour', 3600], ['minute', 60],
]

/**
 * "just now", "5 minutes ago", "in 3 days", "yesterday". Past dates older than a week fall back
 * to an absolute short date (in the store timezone) because "5 weeks ago" is less useful to merchants.
 */
export function relativeTime(d: DateInput, opts: { now?: Date | number; tz?: string; absoluteAfterDays?: number } = {}, locale = defaults.locale): string {
  const v = toDate(d)
  if (!v) return '—'
  const now = typeof opts.now === 'number' ? opts.now : (opts.now ?? new Date()).getTime()
  const diffSec = Math.round((v.getTime() - now) / 1000)
  const abs = Math.abs(diffSec)
  if (abs < 45) return 'just now'
  const absoluteAfter = (opts.absoluteAfterDays ?? 7) * 24 * 3600
  if (diffSec < 0 && abs >= absoluteAfter) return date(v, opts.tz ?? defaults.timeZone, 'short', locale)
  const rtf = cached(`rt|${locale}`, () => new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' })) as Intl.RelativeTimeFormat
  for (const [unit, sec] of REL_UNITS) {
    if (abs >= sec || unit === 'minute') return rtf.format(Math.round(diffSec / sec), unit)
  }
  return rtf.format(Math.round(diffSec / 60), 'minute')
}

/** Short duration: 95 → "1m 35s", 7300 → "2h 1m". */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—'
  const s = Math.max(0, Math.round(seconds))
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60
  if (d) return `${d}d ${h}h`
  if (h) return `${h}h ${m}m`
  if (m) return `${m}m ${sec}s`
  return `${sec}s`
}

/** Initials for avatars: "Asha Rao" → "AR", "hello@x.com" → "H". */
export function initials(name: string | null | undefined): string {
  const s = (name ?? '').trim()
  if (!s) return '?'
  const base = s.includes('@') ? s.split('@')[0] : s
  const words = base.split(/[\s._-]+/).filter(Boolean)
  const chars = words.length >= 2 ? words[0][0] + words[words.length - 1][0] : (words[0] ?? '?').slice(0, s.includes('@') ? 1 : 2)
  return chars.toUpperCase()
}

/** Title-case a snake/kebab status word: "partially_refunded" → "Partially refunded". */
export function humanize(s: string | null | undefined): string {
  if (!s) return ''
  const t = s.replace(/[_-]+/g, ' ').trim()
  return t.charAt(0).toUpperCase() + t.slice(1)
}
